import CardSkeleton from '@ifit/mp-card-skeleton';
import CardUpNext from '@ifit/mp-card-up-next';
import { useContext } from 'react';
import { ThemeContext, ThemeOptions } from '@ifit/themes/ThemeContext';
import { useDeviceType } from '../../state-management/selectors/use-device-type';
import { useParamountItem } from '../../state-management/server-state/use-paramount-item';
import { useUserSeriesDetails } from '../../state-management/server-state/use-user-series-details';
import { useUserWorkoutDetails } from '../../state-management/server-state/use-user-workout-details';
import { getParamountImageSized } from './utils';
import { getUserNavigationHandler, ROWTITLES } from '../../util/navigation-utils';
import { globalizeCompletedDate } from '../../util/date-utils';
import {
  IFormattedMatrixClubWorkout,
  IUpNextItem,
  IUpNextItemType,
  IUserSeriesDetail,
  IUserWorkoutDetail,
} from '@ifit/gateway-wolf-dashboard-service';
import { UseQueryResult } from 'react-query';
import { useMatrixClub } from '../../state-management/server-state/use-matrix-club';
import { useMatrixCommercial } from '../../hooks/use-matrix-commercial';
import { GlobalText } from '../../i18n/global-text';
import { DeviceType } from '../../types';

function useDetails(
  data: IUpNextItem
): UseQueryResult<IUserWorkoutDetail[], Error> | UseQueryResult<IUserSeriesDetail[], Error> | null {
  const userWorkoutDetails = useUserWorkoutDetails(data?.workoutId ? [data.workoutId] : null);
  const userSeriesDetails = useUserSeriesDetails(data?.seriesId ? [data.seriesId] : null);

  switch (data.type) {
    case IUpNextItemType.ContinueSeriesWorkout:
      return userSeriesDetails;
    case IUpNextItemType.LiveScheduledWorkout:
    case IUpNextItemType.RecommendedWorkout:
    case IUpNextItemType.ScheduledWorkout:
    case IUpNextItemType.StaffPickWorkout:
      return userWorkoutDetails;
    default:
      return null;
  }
}

interface ParamountQueryResult {
  data: IUpNextItem;
  error: Error | null;
  isLoading: boolean;
  isSuccess: boolean;
}

export default function ParamountItem(): JSX.Element {
  const deviceType = useDeviceType();
  const { theme } = useContext(ThemeContext);
  const isMatrixCommercial = useMatrixCommercial();
  const paramountQueryResult: ParamountQueryResult = useParamountItem();
  const matrixQueryResult = useMatrixClub(false);
  const detailsQueryResult = useDetails(paramountQueryResult.data);

  const content: JSX.Element = isMatrixCommercial
    ? getParamountMatrixCommercial(matrixQueryResult, deviceType)
    : getParamountStandard(paramountQueryResult, detailsQueryResult, deviceType, theme);

  return <div className="paramount">{content}</div>;
}

function cardSkeleton(): JSX.Element {
  return <CardSkeleton className="custom" />;
}

function getParamountStandard(
  queryResult: ParamountQueryResult,
  detailsQueryResult: UseQueryResult<IUserWorkoutDetail[], Error> | UseQueryResult<IUserSeriesDetail[], Error> | null,
  deviceType: DeviceType,
  theme: ThemeOptions
): JSX.Element {
  const { data, isLoading, isSuccess } = queryResult;
  if (isLoading) {
    return cardSkeleton();
  }

  if (!isSuccess) {
    return null;
  }

  const clickHandler = getUserNavigationHandler(data?.navigationUrl, ROWTITLES.PARAMOUNT_ITEM);

  const completedDate: string =
    Array.isArray(detailsQueryResult?.data) && detailsQueryResult?.data[0]?.completedDate
      ? detailsQueryResult.data[0].completedDate
      : null;
  const lockedStatus: boolean =
    Array.isArray(detailsQueryResult?.data) && detailsQueryResult?.data[0]?.locked
      ? detailsQueryResult.data[0].locked
      : null;

  return (
    <CardUpNext
      imageUrl={getParamountImageSized(data.imageUrl, deviceType)}
      isLive={data.isLive}
      tagPrimary={data.tagPrimary}
      title={data.title}
      averageRating={data.averageRating}
      callToAction={data.callToAction}
      callToActionShort={data.callToActionShort}
      colorScheme={theme === 'VAULT' ? 'vault' : null}
      progress={data?.progress ? data.progress : null}
      leadLine={data.leadLine}
      subtitle={data.subtitle}
      onClick={clickHandler}
      completed={globalizeCompletedDate(completedDate)}
      locked={lockedStatus}
    />
  );
}

function getParamountMatrixCommercial(
  queryResult: UseQueryResult<IFormattedMatrixClubWorkout[], Error>,
  deviceType: DeviceType
): JSX.Element {
  const { data, isLoading, isSuccess } = queryResult;

  if (isLoading) {
    return cardSkeleton();
  }

  if (!isSuccess) {
    return null;
  }

  const {
    images: { largeImage },
    title,
    workout: { url, intensityText, averageRating },
  } = data[0];

  const clickHandler = getUserNavigationHandler(url, ROWTITLES.MATRIX_COMMERCIAL_WORKOUTS);
  const formattedRating = averageRating ? `${averageRating.toFixed(1)}` : null;

  return (
    <CardUpNext
      imageUrl={getParamountImageSized(largeImage, deviceType)}
      isLive={false}
      tagPrimary={intensityText}
      title={title}
      averageRating={formattedRating}
      callToAction={GlobalText.viewDetails}
      onClick={clickHandler}
    />
  );
}
